<template>
  <div class="resident-dictionary">
    <CustomTable
      :title="tableTitle"
      :rowsData="displayedRows"
      :columns="residentColumns"
      :loading="isFetching"
      :show-search="true"
      :custom-body="true"
      :showHeaderAdd="true"
      :dense="true"
      :small="true"
      :small-font="true"
      :on-add-click="handleAddClick"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center">
            {{ props.row.resident_id }}
          </custom-table-cell>

          <custom-table-cell align="left">
            <span style="font-size: 13px;">
              {{ props.row.resident_name }}
            </span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-btn
              v-if="!props.row.guid"
              dense
              size="sm"
              color="primary"
              label="Привязать"
              @click="() => openBindModal(props.row)"
            />
            <span
              v-else-if="props.row.is_fake_guid === true"
              class="clickable"
              style="font-size: 12px; text-decoration: underline; color: #ff9800;"
              @click="() => openBoundInfoModal(props.row)"
            >
              Без привязки
            </span>
            <span
              v-else
              class="clickable"
              style="font-size: 12px; text-decoration: underline; color: #4caf50;"
              @click="() => openBoundInfoModal(props.row)"
            >
              Привязан
            </span>
          </custom-table-cell>

          <custom-table-cell align="left">
            <span v-if="props.row.resident_class_name" style="font-size: 13px;">
              {{ props.row.resident_class_name }}
            </span>
            <span v-else>-</span>
          </custom-table-cell>

          <custom-table-cell align="left">
            <span v-if="props.row.resident_address" style="font-size: 13px;">
              {{ props.row.resident_address }}
            </span>
            <span v-else>-</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-btn
              v-if="props.row.guid"
              dense
              size="sm"
              color="primary"
              label="Подъезды"
              @click="() => openEntrancesModal(props.row)"
            />
            <span v-else style="font-size: 12px; color: #9e9e9e;">-</span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
              <CustomIcon @click="() => handleUpdateClick(props.row)" icon="edit" />
              <CustomIcon
                icon="trash"
                :approve-mode="true"
                :small="true"
                :loading="loading"
                :approve="() => onDeleteSubmit(props.row.resident_id)"
              />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <!-- Модалка создания/редактирования -->
    <q-dialog v-model="modal.show" persistent>
      <q-card style="min-width: 500px">
        <q-card-section>
          <div class="row items-center justify-between">
            <div class="text-h6">{{ modal.title }}</div>
            <q-btn flat dense icon="close" @click="closeModal" />
          </div>
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="modalForm.resident_name"
            dense
            outlined
            label="Название ЖК *"
            :rules="[val => !!val || 'Поле обязательно']"
            class="q-mb-md"
          />

          <q-select
            v-model="modalForm.city_id"
            :options="cityOptions"
            option-value="city_id"
            option-label="city_name"
            emit-value
            map-options
            dense
            outlined
            placeholder="Выберите город *"
            :rules="[val => !!val || 'Поле обязательно']"
            class="q-mb-md"
          />

          <q-select
            v-model="modalForm.resident_class_id"
            :options="classOptions"
            option-value="resident_class_id"
            option-label="resident_class_name"
            emit-value
            map-options
            dense
            outlined
            label="Класс ЖК *"
            :rules="[val => !!val || 'Поле обязательно']"
            class="q-mb-md"
          />

          <q-input
            v-model="modalForm.resident_address"
            dense
            outlined
            label="Адрес"
            class="q-mb-md"
          />
        </q-card-section>

        <q-card-actions align="right" class="q-gutter-sm">
          <q-btn 
            unelevated 
            dense 
            :loading="isSaving" 
            color="primary" 
            label="Сохранить" 
            @click="saveResident" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка подъездов -->
    <q-dialog v-model="entrancesModal.show" persistent>
      <q-card style="min-width: 80vw; max-width: 90vw;" class="entrances-modal-card">
        <q-card-section>
          <div class="row items-center justify-between">
            <div class="text-h6">Подъезды - {{ entrancesModal.residentName }}</div>
            <q-btn flat dense icon="close" @click="closeEntrancesModal" />
          </div>
        </q-card-section>

        <q-card-section v-if="entrancesModal.loading" class="text-center">
          <q-spinner color="primary" size="3em" />
        </q-card-section>

        <q-card-section v-else>
          <div v-if="getEntrancesList().length === 0" class="text-center q-pa-lg">
            <q-icon name="info" size="48px" color="grey-5" />
            <div class="q-mt-md text-grey-6">Подъезды не найдены</div>
            <q-btn
              unelevated
              color="primary"
              icon="add"
              label="Добавить первый подъезд"
              @click="addEntrance"
              class="q-mt-md"
            />
          </div>
          
          <!-- Таблица Excel-стиль: этажи - строки, подъезды - столбцы -->
          <div v-else class="excel-table-container">
            <div class="excel-table-wrapper" ref="tableScrollContainer">
              <table class="excel-table">
              <thead>
                <tr>
                  <!-- Первая ячейка заголовка - иконка ЖК -->
                  <th class="excel-header-cell">
                    <q-icon name="home" size="24px" color="primary" />
                  </th>
                    <!-- Заголовки подъездов -->
                    <th
                      v-for="entrance in getEntrancesList()"
                      :key="entrance"
                      class="excel-header-cell entrance-header-cell"
                    >
                      <div class="entrance-header-content" @click="editEntrance(entrance)">
                        <span class="entrance-title clickable">Подъезд {{ entrance }}</span>
                      </div>
                    </th>
                    <!-- Кнопка добавления подъезда -->
                    <th class="excel-header-cell add-entrance-header" @click="addEntrance">
                      <q-icon name="add" size="20px" />
                      <div>Добавить подъезд</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Строка для добавления квартир на верхнем этаже -->
                  <tr class="excel-row add-flats-row">
                    <td class="excel-cell floor-cell add-flats-header-cell">
                      <div class="add-flats-label">Добавить квартиру</div>
                    </td>
                    <!-- Кнопки добавления квартир для каждого подъезда -->
                    <td
                      v-for="entrance in getEntrancesList()"
                      :key="`add-${entrance}`"
                      class="excel-cell add-flats-cell"
                    >
                      <q-btn
                        flat
                        dense
                        size="sm"
                        icon="add"
                        color="primary"
                        label="Добавить"
                        @click="addFlatToTopFloor(entrance)"
                        class="add-flat-btn"
                      />
                    </td>
                    <!-- Пустая ячейка в конце строки с кнопкой копирования -->
                    <td class="excel-cell empty-cell">
                      <q-btn
                        v-if="getEntrancesList().length > 0 && getEntrancesList().some(e => !isEntranceEmpty(e))"
                        flat
                        dense
                        size="sm"
                        icon="content_copy"
                        color="secondary"
                        label="Копировать в новый"
                        @click="openCopyEntranceModal(null)"
                        class="copy-entrance-btn"
                      />
                    </td>
                  </tr>
                  
                  <!-- Строки с этажами -->
                  <tr
                    v-for="floor in getAllFloors()"
                    :key="floor"
                    class="excel-row"
                    :class="{ 'row-highlighted': highlightedFloor === floor }"
                    @mouseenter="highlightFloorRow(floor)"
                    @mouseleave="unhighlightFloorRow()"
                  >
                    <!-- Ячейка с номером этажа -->
                    <td
                      class="excel-cell floor-cell clickable"
                      @click="editFloorByNumber(floor)"
                    >
                      <div class="floor-number">{{ getFloorDisplayName(floor) }}</div>
                    </td>
                    <!-- Ячейки с квартирами для каждого подъезда -->
                    <td
                      v-for="entrance in getEntrancesList()"
                      :key="`${entrance}-${floor}`"
                      class="excel-cell flats-cell clickable"
                      :class="{ 
                        'has-flats': getFlatsForEntranceFloor(entrance, floor) !== '-',
                        'flats-cell-hovered': highlightedFlatsCell === `${entrance}-${floor}`
                      }"
                      @mouseenter="highlightFlatsCell(`${entrance}-${floor}`)"
                      @mouseleave="unhighlightFlatsCell()"
                      @click="openEditFlatsModal(entrance, floor)"
                    >
                      <div class="row items-center justify-between no-wrap full-width">
                        <div class="flats-in-floor">
                          {{ getFlatsForEntranceFloor(entrance, floor) }}
                        </div>
                        <q-btn
                          v-if="getFlatsForEntranceFloor(entrance, floor) !== '-'"
                          flat
                          dense
                          round
                          icon="delete"
                          color="negative"
                          size="sm"
                          @click.stop="deleteFloorConfirm(entrance, floor)"
                        />
                      </div>
                    </td>
                    <!-- Пустая ячейка в конце строки -->
                    <td class="excel-cell empty-cell"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Модалка редактирования подъезда -->
    <q-dialog v-model="editEntranceModal.show" persistent>
      <q-card style="min-width: 400px;">
        <q-card-section>
          <div class="text-h6">Редактировать подъезд</div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="editEntranceModal.entrance"
            label="Номер подъезда"
            outlined
            dense
          />
        </q-card-section>
        <q-card-actions>
          <q-btn flat color="negative" label="Удалить" @click="confirmDeleteEntranceFromModal" />
          <q-space />
          <q-btn flat label="Отмена" @click="editEntranceModal.show = false" />
          <q-btn unelevated color="primary" label="Сохранить" @click="saveEntranceEdit" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка добавления подъезда -->
    <q-dialog v-model="addEntranceModal.show" persistent>
      <q-card style="min-width: 400px;">
        <q-card-section>
          <div class="row items-center justify-between">
            <div class="text-h6">Добавить подъезд</div>
            <q-btn flat dense icon="close" @click="addEntranceModal.show = false" />
          </div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="addEntranceModal.entrance"
            label="Номер подъезда"
            outlined
            dense
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn unelevated color="primary" label="Добавить" @click="saveAddEntrance" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка редактирования этажа -->
    <q-dialog v-model="editFloorModal.show" persistent>
      <q-card style="min-width: 400px;">
        <q-card-section>
          <div class="text-h6">Редактировать этаж</div>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="editFloorModal.floor"
            label="Номер этажа"
            outlined
            dense
          />
        </q-card-section>
        <q-card-actions>
          <q-btn flat color="negative" label="Удалить" @click="confirmDeleteFloorFromModal" />
          <q-space />
          <q-btn flat label="Отмена" @click="editFloorModal.show = false" />
          <q-btn unelevated color="primary" label="Сохранить" @click="saveFloorEdit" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка добавления этажа -->
    <q-dialog v-model="addFloorModal.show" persistent>
      <q-card style="min-width: 400px;">
        <q-card-section>
          <div class="text-h6">Добавить этаж</div>
        </q-card-section>
        <q-card-section>
          <q-select
            v-model="addFloorModal.entrance"
            :options="getEntrancesList()"
            label="Выберите подъезд"
            outlined
            dense
            emit-value
            map-options
          />
          <q-input
            v-model="addFloorModal.floor"
            label="Номер этажа"
            outlined
            dense
            class="q-mt-md"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" @click="addFloorModal.show = false" />
          <q-btn unelevated color="primary" label="Добавить" @click="saveAddFloor" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка добавления квартиры -->
    <q-dialog v-model="addFlatModal.show" persistent>
      <q-card style="min-width: 600px;">
        <q-card-section>
          <div class="row items-center justify-between">
            <div>
              <div class="text-h6">Добавить квартиры</div>
              <div class="text-caption text-grey-7 q-mt-sm">Подъезд: {{ addFlatModal.entrance }}</div>
            </div>
            <q-btn flat dense icon="close" @click="closeAddFlatModal" />
          </div>
        </q-card-section>
        <q-card-section>
          <table class="flats-input-table">
            <thead>
              <tr>
                <th>Этаж</th>
                <th>Тип</th>
                <th>Номер квартиры</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in addFlatModal.items" :key="index">
                <td>
                  <q-input
                    v-model="item.floor"
                    outlined
                    dense
                    placeholder="Этаж"
                    :readonly="isRoofOrBasement(item.floor)"
                  />
                </td>
                <td>
                  <q-toggle
                    v-model="item.is_mop"
                    label="МОП"
                    color="primary"
                    :disable="isRoofOrBasement(item.floor)"
                    @update:model-value="onMopToggle(item, index)"
                  />
                </td>
                <td>
                  <q-input
                    v-if="item.is_mop || isRoofOrBasement(item.floor)"
                    :model-value="'МОП'"
                    outlined
                    dense
                    readonly
                  />
                  <q-input
                    v-else
                    v-model="item.flat_num"
                    outlined
                    dense
                    placeholder="Номер квартиры"
                  />
                </td>
                <td>
                  <q-btn
                    flat
                    dense
                    round
                    icon="delete"
                    color="negative"
                    size="sm"
                    @click="removeFlatInputRow(index)"
                    :disable="addFlatModal.items.length === 1"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div class="column q-gutter-sm q-mt-md items-start">
            <q-btn
              flat
              dense
              icon="add"
              label="Добавить квартиру"
              color="primary"
              @click="addFlatInputRow"
            />
            <q-btn
              flat
              dense
              label="Добавить кровлю"
              color="primary"
              @click="addRoof"
              :disable="hasRoof"
            />
            <q-btn
              flat
              dense
              label="Добавить подвал"
              color="primary"
              @click="addBasement"
              :disable="hasBasement"
            />
          </div>
        </q-card-section>
        <q-card-actions>
          <q-space />
          <q-btn unelevated color="primary" label="Добавить" @click="saveAddFlat" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка редактирования квартир на этаже -->
    <q-dialog v-model="editFlatsModal.show" persistent>
      <q-card style="min-width: 600px;">
        <q-card-section>
          <div class="text-h6">Редактировать квартиры</div>
          <div class="text-caption text-grey-7 q-mt-sm">
            Подъезд: {{ editFlatsModal.entrance }}, {{ getFloorDisplayName(editFlatsModal.floor) }}
          </div>
        </q-card-section>
        <q-card-section>
          <table class="flats-input-table">
            <thead>
              <tr>
                <th>Тип</th>
                <th>Номер квартиры</th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in editFlatsModal.items" :key="index">
                <td>
                  <q-toggle
                    v-model="item.is_mop"
                    label="МОП"
                    color="primary"
                    @update:model-value="onEditMopToggle(item, index)"
                  />
                </td>
                <td>
                  <q-input
                    v-if="item.is_mop"
                    :model-value="'МОП'"
                    outlined
                    dense
                    readonly
                  />
                  <q-input
                    v-else
                    v-model="item.flat_num"
                    outlined
                    dense
                    placeholder="Номер квартиры"
                  />
                </td>
                <td>
                  <q-btn
                    flat
                    dense
                    round
                    icon="delete"
                    color="negative"
                    size="sm"
                    @click="removeEditFlatRow(index)"
                    :disable="editFlatsModal.items.length === 1"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <q-btn
            flat
            dense
            icon="add"
            label="Добавить квартиру"
            color="primary"
            @click="addEditFlatRow"
            class="q-mt-md"
          />
        </q-card-section>
        <q-card-actions>
          <q-space />
          <q-btn flat label="Отмена" @click="editFlatsModal.show = false" />
          <q-btn unelevated color="primary" label="Сохранить" @click="saveEditFlats" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка привязки ЖК -->
    <q-dialog v-model="bindModal.show" persistent>
      <q-card style="min-width: 700px; max-width: 900px;">
        <q-card-section>
          <div class="row items-center justify-between">
            <div>
              <div class="text-h6">Привязка ЖК</div>
              <div class="text-caption text-grey-7 q-mt-sm">
                {{ bindModal.currentResident?.guid ? 'Изменить привязку для' : 'Выберите ЖК для привязки к' }}: {{ bindModal.currentResident?.resident_name }}
              </div>
            </div>
            <q-btn flat dense icon="close" @click="closeBindModal" />
          </div>
        </q-card-section>
        <q-card-section>
          <div class="row items-center q-mb-md">
            <q-checkbox
              v-model="bindModal.showOnlyUnbound"
              label="ЖК без привязки"
              color="primary"
              class="q-mr-md"
            />
            <q-input
              v-model="bindModal.search"
              outlined
              dense
              placeholder="Поиск по названию ЖК..."
              style="flex: 1;"
              clearable
            >
              <template v-slot:prepend>
                <q-icon name="search" />
              </template>
            </q-input>
          </div>
          <div v-if="bindModal.loading" class="text-center q-pa-md">
            <q-spinner color="primary" size="3em" />
          </div>
          <div v-else style="max-height: 400px; overflow-y: auto;">
            <q-table
              :rows="filteredResidentsList"
              :columns="bindModalColumns"
              row-key="resident_id"
              flat
              dense
              hide-pagination
              :rows-per-page-options="[0]"
              @row-click="(evt, row) => selectResident(row)"
            >
              <template v-slot:body="props">
                <q-tr
                  :props="props"
                  :class="{ 'selected-row': bindModal.selectedResident && bindModal.selectedResident.resident_id === props.row.resident_id }"
                  class="cursor-pointer"
                  @click="selectResident(props.row)"
                >
                  <q-td
                    v-for="col in props.cols"
                    :key="col.name"
                    :props="props"
                  >
                    <template v-if="col.name === 'is_bound'">
                      <q-chip
                        :label="props.row.is_bound"
                        :color="props.row.is_bound === 'Да' ? 'positive' : 'grey'"
                        text-color="white"
                        size="sm"
                        dense
                      />
                    </template>
                    <template v-else>
                      {{ col.value || '-' }}
                    </template>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
            <div v-if="filteredResidentsList.length === 0" class="text-center q-pa-md text-grey">
              ЖК не найдены
            </div>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            v-if="bindModal.currentResident && bindModal.currentResident.guid"
            flat
            color="negative"
            label="Отвязать"
            @click="unbindResidentFromModal"
          />
          <q-btn
            flat
            color="secondary"
            label="без привязки"
            @click="createGuidWithoutBindingFromModal"
          />
          <q-btn
            unelevated
            color="primary"
            label="Привязать"
            :disable="!bindModal.selectedResident"
            @click="confirmBindResident"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка информации о привязке -->
    <q-dialog v-model="boundInfoModal.show" persistent>
      <q-card style="min-width: 500px;">
        <q-card-section>
          <div class="row items-center justify-between">
            <div class="text-h6">Информация о привязке</div>
            <q-btn flat dense icon="close" @click="boundInfoModal.show = false" />
          </div>
        </q-card-section>
        <q-card-section v-if="boundInfoModal.loading">
          <div class="text-center q-pa-md">
            <q-spinner color="primary" size="3em" />
          </div>
        </q-card-section>
        <q-card-section v-else-if="boundInfoModal.currentResident">
          <q-table
            :rows="[boundInfoModal.currentResident]"
            :columns="boundInfoModalColumns"
            row-key="resident_id"
            flat
            dense
            hide-pagination
            :rows-per-page-options="[0]"
          >
            <template v-slot:body-cell-resident_name="props">
              <q-td :props="props">
                {{ props.value || '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-resident_class_name="props">
              <q-td :props="props">
                {{ props.value || '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-resident_address="props">
              <q-td :props="props">
                {{ props.value || '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-guid="props">
              <q-td :props="props">
                {{ props.value || '-' }}
              </q-td>
            </template>
          </q-table>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            unelevated
            label="Отвязать"
            color="negative"
            @click="unbindResident"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Модалка копирования подъезда -->
    <q-dialog v-model="copyEntranceModal.show" persistent>
      <q-card style="min-width: 500px;">
        <q-card-section>
          <div class="row items-center justify-between">
            <div class="text-h6">Копировать квартиры</div>
            <q-btn flat dense icon="close" @click="copyEntranceModal.show = false" />
          </div>
        </q-card-section>
        <q-card-section>
          <div class="q-mb-md">
            <div class="text-body2 q-mb-sm">Выберите подъезд для копирования:</div>
            <q-select
              v-model="copyEntranceModal.sourceEntrance"
              :options="copyEntranceModal.availableEntrances"
              option-label="label"
              option-value="value"
              emit-value
              map-options
              dense
              outlined
              label="Подъезд-источник"
            />
          </div>
          <div class="q-mb-md">
            <q-input
              v-model="copyEntranceModal.targetEntrance"
              dense
              outlined
              label="Номер нового подъезда"
              :rules="[val => !!val || 'Введите номер подъезда']"
            />
          </div>
          <div class="text-caption text-grey-7">
            Квартиры из выбранного подъезда будут скопированы в подъезд {{ copyEntranceModal.targetEntrance || 'новый' }}.
            Номера квартир будут продолжены от последней квартиры последнего этажа.
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" @click="copyEntranceModal.show = false" />
          <q-btn
            flat
            color="primary"
            label="Копировать"
            :disable="!copyEntranceModal.sourceEntrance || !copyEntranceModal.targetEntrance"
            @click="confirmCopyEntrance"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from '@/services/store/app';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import {
  getResidentList,
  saveResidentItem,
  deleteResidentItem,
  getResidentClasses,
  getResidentCities,
  getEntrancesData,
  saveEntrance,
  deleteEntrance,
  deleteFloor,
  getResidentsForBind,
  residentColumns,
  copyEntranceFlats
} from "@/views/ResidentDictionary/services";

export default {
  name: "ResidentDictionary",
  components: {
    CustomButton,
    CustomTable,
    CustomTableCell,
    CustomIcon
  },
  data() {
    return {
      residentColumns,
      rowsData: [],
      allRowsData: [], // Все загруженные данные
      displayedCount: 50, // Количество отображаемых записей
      classes: [],
      isFetching: false,
      isSaving: false,
      loading: false,
      modal: {
        show: false,
        title: "",
        mode: "create" // create or edit
      },
      modalForm: {
        resident_id: null,
        resident_name: "",
        city_id: null,
        resident_class_id: null,
        resident_address: ""
      },
      bindModal: {
        show: false,
        currentResident: null,
        residentsList: [],
        loading: false,
        search: ""
      },
      boundInfoModal: {
        show: false,
        currentResident: null,
        boundResident: null,
        loading: false
      },
      entrancesModal: {
        show: false,
        residentId: null,
        residentGuid: null,
        residentName: "",
        loading: false
      },
      entrancesData: [],
      entrancesStructure: {}, // { entrance: { floors: [{ floor, flat_nums, flat_ids }] } }
      isSyncingScroll: false, // Флаг для предотвращения бесконечного цикла синхронизации скролла
      editEntranceModal: {
        show: false,
        entrance: "",
        originalEntrance: ""
      },
      addEntranceModal: {
        show: false,
        entrance: ""
      },
      editFloorModal: {
        show: false,
        entrance: "",
        floor: "",
        originalFloor: ""
      },
      addFloorModal: {
        show: false,
        entrance: null,
        floor: ""
      },
      addFlatModal: {
        show: false,
        entrance: null,
        items: [{ floor: "", flat_num: "" }]
      },
      editFlatsModal: {
        show: false,
        entrance: null,
        floor: "",
        items: []
      },
      copyEntranceModal: {
        show: false,
        targetEntrance: null, // Подъезд, в который копируем
        sourceEntrance: null, // Подъезд, из которого копируем
        availableEntrances: [] // Список доступных подъездов для копирования
      },
      highlightedFloor: null, // Для подсветки строки этажа
      highlightedFlatsCell: null, // Для подсветки ячейки с квартирами
      bindModal: {
        show: false,
        currentResident: null,
        selectedResident: null,
        residentsList: [],
        loading: false,
        search: "",
        showOnlyUnbound: false
      },
      boundInfoModal: {
        show: false,
        currentResident: null,
        boundResident: null,
        loading: false
      },
      bindModalColumns: [
        {
          name: "resident_name",
          label: "Наименование",
          field: "resident_name",
          align: "left",
          sortable: true
        },
        {
          name: "resident_class_name",
          label: "Класс",
          field: "resident_class_name",
          align: "left",
          sortable: true
        },
        {
          name: "resident_address",
          label: "Адрес",
          field: "resident_address",
          align: "left",
          sortable: true
        },
        {
          name: "guid",
          label: "GUID",
          field: "guid",
          align: "left",
          sortable: true
        },
        {
          name: "is_bound",
          label: "Привязан",
          field: "is_bound",
          align: "center",
          sortable: true
        }
      ],
      boundInfoModalColumns: [
        {
          name: "resident_name",
          label: "Наименование",
          field: "resident_name",
          align: "left",
          sortable: false
        },
        {
          name: "resident_class_name",
          label: "Класс",
          field: "resident_class_name",
          align: "left",
          sortable: false
        },
        {
          name: "guid",
          label: "GUID",
          field: "guid",
          align: "left",
          sortable: false
        }
      ]
    };
  },
  computed: {
    cityOptions() {
      return this.cities || [];
    },
    classOptions() {
      return this.classes || [];
    },
    displayedRows() {
      return this.allRowsData.slice(0, this.displayedCount);
    },
    tableTitle() {
      const count = this.allRowsData?.length || 0;
      return `Справочник ЖК (${count})`;
    },
    filteredResidentsList() {
      let filtered = this.bindModal.residentsList;
      
      // Фильтр по поиску
      if (this.bindModal.search) {
        const search = this.bindModal.search.toLowerCase();
        filtered = filtered.filter(r => 
          r.resident_name?.toLowerCase().includes(search) ||
          r.guid?.toLowerCase().includes(search) ||
          r.resident_class_name?.toLowerCase().includes(search) ||
          r.resident_address?.toLowerCase().includes(search)
        );
      }
      
      // Фильтр "ЖК без привязки"
      if (this.bindModal.showOnlyUnbound) {
        filtered = filtered.filter(r => r.is_bound === 'Нет');
      }
      
      return filtered;
    },
    hasRoof() {
      if (!this.addFlatModal.entrance) return false;
      // Проверяем в текущей модалке
      const hasInModal = this.addFlatModal.items.some(item => String(item.floor) === '100');
      if (hasInModal) return true;
      // Проверяем в существующей структуре
      const floors = this.entrancesStructure[this.addFlatModal.entrance]?.floors || [];
      return floors.some(f => String(f.floor) === '100');
    },
    hasBasement() {
      if (!this.addFlatModal.entrance) return false;
      // Проверяем в текущей модалке
      const hasInModal = this.addFlatModal.items.some(item => String(item.floor) === '-1');
      if (hasInModal) return true;
      // Проверяем в существующей структуре
      const floors = this.entrancesStructure[this.addFlatModal.entrance]?.floors || [];
      return floors.some(f => String(f.floor) === '-1');
    }
  },
  methods: {
    async loadDictionaries() {
      const classes = await getResidentClasses();
      if (classes) {
        this.classes = classes;
      }
    },
    async fetchResidents() {
      this.isFetching = true;
      const cityId = this.userData?.city_id;
      const params = cityId ? { city_id: cityId } : {};
      const data = await getResidentList(params);
      this.isFetching = false;
      this.allRowsData = data || [];
      this.rowsData = this.allRowsData;
      this.displayedCount = 50; // Сбрасываем счетчик при новой загрузке
    },
    loadMoreRows() {
      if (this.displayedCount < this.allRowsData.length) {
        this.displayedCount += 50;
      }
    },
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Загружаем следующую порцию когда до конца осталось 200px
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMoreRows();
      }
    },
    handleAddClick() {
      this.modal.mode = "create";
      this.modal.title = "Создать ЖК";
      this.modalForm = {
        resident_id: null,
        resident_name: "",
        city_id: this.userData?.city_id || null,
        resident_class_id: null,
        resident_address: ""
      };
      this.modal.show = true;
    },
    handleUpdateClick(row) {
      this.modal.mode = "edit";
      this.modal.title = "Редактировать ЖК";
      this.modalForm = {
        resident_id: row.resident_id,
        resident_name: row.resident_name || "",
        city_id: row.city_id,
        resident_class_id: row.resident_class_id,
        resident_address: row.resident_address || ""
      };
      this.modal.show = true;
    },
    closeModal() {
      this.modal.show = false;
      this.modalForm = {
        resident_id: null,
        resident_name: "",
        city_id: null,
        resident_class_id: null,
        resident_address: ""
      };
    },
    async saveResident() {
      if (!this.modalForm.resident_name || !this.modalForm.city_id || !this.modalForm.resident_class_id) {
        this.showWarningSnackbar("Заполните обязательные поля");
        return;
      }

      this.isSaving = true;
      const payload = {
        resident_id: this.modalForm.resident_id,
        resident_name: this.modalForm.resident_name,
        city_id: this.modalForm.city_id,
        resident_class_id: this.modalForm.resident_class_id,
        is_active: 1,
        resident_address: this.modalForm.resident_address || null,
      };

      const res = await saveResidentItem(payload);
      this.isSaving = false;

      if (res?.message) {
        this.showSucceedSnackbar(res.message);
        this.closeModal();
        await this.fetchResidents();
        // Прокручиваем вверх после сохранения
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    async onDeleteSubmit(residentId) {
      this.loading = true;
      const res = await deleteResidentItem(residentId);
      this.loading = false;
      
      if (res) {
        this.showSucceedSnackbar("ЖК деактивирован");
        await this.fetchResidents();
      }
    },
    async openEntrancesModal(row) {
      this.entrancesModal.residentId = row.resident_id;
      this.entrancesModal.residentGuid = row.guid;
      this.entrancesModal.residentName = row.resident_name || "";
      this.entrancesModal.show = true;
      await this.loadEntrancesData();
    },
    closeEntrancesModal() {
      this.entrancesModal.show = false;
      this.entrancesModal.residentId = null;
      this.entrancesModal.residentGuid = null;
      this.entrancesModal.residentName = "";
      this.entrancesData = [];
      this.entrancesStructure = {};
    },
    async loadEntrancesData() {
      if (!this.entrancesModal.residentGuid) return;
      
      this.entrancesModal.loading = true;
      try {
        const data = await getEntrancesData(this.entrancesModal.residentGuid);
        this.entrancesData = data || [];
        
        // Группируем по подъездам
        this.entrancesStructure = {};
        this.entrancesData.forEach(item => {
          if (!this.entrancesStructure[item.entrance]) {
            this.entrancesStructure[item.entrance] = { floors: [] };
          }
          // Обрабатываем flat_nums - может быть массивом или строкой
          let flatNums = item.flat_nums || [];
          if (typeof flatNums === 'string') {
            try {
              flatNums = JSON.parse(flatNums);
            } catch (e) {
              flatNums = [];
            }
          }
          if (!Array.isArray(flatNums)) {
            flatNums = [];
          }
          
          // Обрабатываем flat_ids - может быть массивом или строкой
          let flatIds = item.flat_ids || [];
          if (typeof flatIds === 'string') {
            // PostgreSQL массивы приходят как "{1,2,3}" или как JSON "[1,2,3]"
            if (flatIds.startsWith('{') && flatIds.endsWith('}')) {
              // PostgreSQL формат массива: "{1,2,3}" -> [1,2,3]
              try {
                const cleaned = flatIds.slice(1, -1); // Убираем фигурные скобки
                if (cleaned.trim() === '') {
                  flatIds = [];
                } else {
                  flatIds = cleaned.split(',').map(id => {
                    const trimmed = id.trim();
                    return trimmed === 'NULL' || trimmed === '' ? null : parseInt(trimmed);
                  });
                }
              } catch (e) {
                flatIds = [];
              }
            } else {
              // JSON формат: "[1,2,3]"
              try {
                flatIds = JSON.parse(flatIds);
              } catch (e) {
                flatIds = [];
              }
            }
          }
          if (!Array.isArray(flatIds)) {
            flatIds = [];
          }
          
          this.entrancesStructure[item.entrance].floors.push({
            floor: item.floor,
            flat_nums: flatNums,
            flat_ids: flatIds
          });
        });
      } catch (e) {
        console.error("Error loading entrances:", e);
        this.showErrorSnackbar("Ошибка загрузки данных подъездов");
      } finally {
        this.entrancesModal.loading = false;
      }
    },
    getEntrancesList() {
      return Object.keys(this.entrancesStructure).sort((a, b) => parseInt(a) - parseInt(b));
    },
    isEntranceEmpty(entrance) {
      const floors = this.entrancesStructure[entrance]?.floors || [];
      return floors.length === 0 || floors.every(f => !f.flat_nums || f.flat_nums.length === 0);
    },
    openCopyEntranceModal(targetEntrance) {
      this.copyEntranceModal.targetEntrance = targetEntrance;
      // Получаем список подъездов, которые не пустые и не являются целевым подъездом
      const allEntrances = this.getEntrancesList();
      this.copyEntranceModal.availableEntrances = allEntrances
        .filter(entrance => entrance !== targetEntrance && !this.isEntranceEmpty(entrance))
        .map(entrance => ({
          label: `Подъезд ${entrance}`,
          value: entrance
        }));
      
      if (this.copyEntranceModal.availableEntrances.length === 0) {
        this.showWarningSnackbar("Нет подъездов для копирования");
        return;
      }
      
      // Если targetEntrance = null, значит создаем новый подъезд
      if (targetEntrance === null) {
        // Находим следующий свободный номер подъезда
        const existingEntrances = allEntrances.map(e => parseInt(e)).filter(e => !isNaN(e));
        let nextEntrance = 1;
        while (existingEntrances.includes(nextEntrance)) {
          nextEntrance++;
        }
        this.copyEntranceModal.targetEntrance = nextEntrance.toString();
      }
      
      this.copyEntranceModal.sourceEntrance = null;
      this.copyEntranceModal.show = true;
    },
    async confirmCopyEntrance() {
      console.log("confirmCopyEntrance called", {
        sourceEntrance: this.copyEntranceModal.sourceEntrance,
        targetEntrance: this.copyEntranceModal.targetEntrance,
        residentGuid: this.entrancesModal.residentGuid
      });
      
      if (!this.copyEntranceModal.sourceEntrance || !this.copyEntranceModal.targetEntrance) {
        this.showWarningSnackbar("Выберите подъезд для копирования");
        return;
      }
      
      // Проверяем, не существует ли уже подъезд с таким номером
      if (this.entrancesStructure[this.copyEntranceModal.targetEntrance] && this.copyEntranceModal.targetEntrance !== this.copyEntranceModal.sourceEntrance) {
        this.showWarningSnackbar("Подъезд с таким номером уже существует");
        return;
      }
      
      try {
        const result = await copyEntranceFlats({
          resident_guid: this.entrancesModal.residentGuid,
          source_entrance: this.copyEntranceModal.sourceEntrance,
          target_entrance: this.copyEntranceModal.targetEntrance
        });
        
        console.log("Copy result:", result);
        
        this.showSucceedSnackbar("Квартиры скопированы");
        this.copyEntranceModal.show = false;
        await this.loadEntrancesData();
      } catch (e) {
        console.error("Error copying entrance:", e);
        const errorMessage = e?.response?.data?.message || e?.message || "Ошибка копирования квартир";
        this.showErrorSnackbar(errorMessage);
      }
    },
    getFloorsForEntrance(entrance) {
      return this.entrancesStructure[entrance]?.floors || [];
    },
    getAllFloors() {
      // Собираем все уникальные этажи из всех подъездов
      const allFloors = new Set();
      Object.keys(this.entrancesStructure).forEach(entrance => {
        const floors = this.entrancesStructure[entrance]?.floors || [];
        floors.forEach(floorItem => {
          allFloors.add(floorItem.floor);
        });
      });
      // Сортируем по убыванию (последний этаж первый в массиве = сверху, 1 этаж последний = внизу)
      return Array.from(allFloors).sort((a, b) => parseInt(b) - parseInt(a));
    },
    getFloorDisplayName(floor) {
      const floorStr = String(floor);
      if (floorStr === '100') {
        return 'Кровля';
      } else if (floorStr === '-1') {
        return 'Подвал';
      } else {
        return `Этаж ${floor}`;
      }
    },
    getFlatsForEntranceFloor(entrance, floor) {
      const floors = this.entrancesStructure[entrance]?.floors || [];
      const floorItem = floors.find(f => f.floor === floor);
      const flatNums = floorItem?.flat_nums || [];
      if (!floorItem || !flatNums || flatNums.length === 0) {
        return "-";
      }
      return this.getFlatsRange(entrance, floor);
    },
    editFloorByNumber(floor) {
      // Находим первый подъезд с этим этажом для редактирования
      const entrances = this.getEntrancesList();
      for (const entrance of entrances) {
        const floors = this.entrancesStructure[entrance]?.floors || [];
        const floorItem = floors.find(f => f.floor === floor);
        if (floorItem) {
          this.editFloor(entrance, floorItem);
          return;
        }
      }
      // Если этаж не найден, предлагаем добавить
      this.showWarningSnackbar("Этаж не найден в подъездах");
    },
    highlightFloorRow(floor) {
      // Подсвечиваем строку только если не подсвечена ячейка с квартирами
      if (!this.highlightedFlatsCell) {
        this.highlightedFloor = floor;
      }
    },
    unhighlightFloorRow() {
      // Убираем подсветку строки только если не подсвечена ячейка с квартирами
      if (!this.highlightedFlatsCell) {
        this.highlightedFloor = null;
      }
    },
    highlightFlatsCell(cellKey) {
      this.highlightedFlatsCell = cellKey;
      // Убираем подсветку строки при подсветке ячейки
      this.highlightedFloor = null;
    },
    unhighlightFlatsCell() {
      this.highlightedFlatsCell = null;
    },
    addEntrance() {
      this.addEntranceModal.entrance = "";
      this.addEntranceModal.show = true;
    },
    saveAddEntrance() {
      const entranceNum = this.addEntranceModal.entrance;
      if (!entranceNum || !entranceNum.trim()) {
        this.showWarningSnackbar("Введите номер подъезда");
        return;
      }
      
      if (this.entrancesStructure[entranceNum]) {
        this.showWarningSnackbar("Подъезд с таким номером уже существует");
        return;
      }
      
      // Создаем подъезд с пустым массивом этажей (не сохраняем сразу, т.к. floors_data обязателен)
      this.entrancesStructure[entranceNum] = { floors: [] };
      // Не вызываем saveEntranceStructure, т.к. floors_data не может быть пустым
      // Подъезд будет сохранен при добавлении первого этажа/квартиры
      this.addEntranceModal.show = false;
    },
    async deleteEntranceItem(entrance) {
      this.$q.dialog({
        title: 'Подтверждение',
        message: `Удалить подъезд ${entrance}?`,
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          await deleteEntrance({
            resident_guid: this.entrancesModal.residentGuid,
            entrance: entrance
          });
          delete this.entrancesStructure[entrance];
          this.showSucceedSnackbar("Подъезд удален");
        } catch (e) {
          this.showErrorSnackbar("Ошибка удаления подъезда");
        }
      });
    },
    async addFloor(entrance) {
      if (!entrance) {
        this.showAddFloorDialog();
        return;
      }
      
      this.addFloorModal.entrance = entrance;
      this.addFloorModal.floor = "";
      this.addFloorModal.show = true;
    },
    showAddFloorDialog() {
      const entrances = this.getEntrancesList();
      if (entrances.length === 0) {
        this.showWarningSnackbar("Сначала добавьте подъезд");
        return;
      }
      this.addFloorModal.entrance = entrances[0];
      this.addFloorModal.floor = "";
      this.addFloorModal.show = true;
    },
    async saveAddFloor() {
      if (!this.addFloorModal.entrance || !this.addFloorModal.floor) {
        this.showWarningSnackbar("Заполните все поля");
        return;
      }
      
      const floors = this.entrancesStructure[this.addFloorModal.entrance].floors;
      const existingFloor = floors.find(f => f.floor === this.addFloorModal.floor);
      if (existingFloor) {
        this.showWarningSnackbar("Этаж с таким номером уже существует");
        return;
      }
      
      floors.push({
        floor: this.addFloorModal.floor,
        flat_nums: [],
        flat_ids: []
      });
      
      // Сортируем этажи по убыванию
      floors.sort((a, b) => parseInt(b.floor) - parseInt(a.floor));
      
      this.addFloorModal.show = false;
      await this.saveEntranceStructure(this.addFloorModal.entrance);
    },
    async deleteFloor(entrance, floor) {
      this.$q.dialog({
        title: 'Подтверждение',
        message: `Удалить этаж ${floor}?`,
        cancel: true,
        persistent: true
      }).onOk(async () => {
        const floors = this.entrancesStructure[entrance].floors;
        const index = floors.findIndex(f => f.floor === floor);
        if (index !== -1) {
          floors.splice(index, 1);
          await this.saveEntranceStructure(entrance);
        }
      });
    },
    async deleteFloorConfirm(entrance, floor) {
      const floorName = this.getFloorDisplayName(floor);
      this.$q.dialog({
        title: 'Подтверждение',
        message: `Удалить все квартиры этажа ${floorName} в подъезде ${entrance}?`,
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          await deleteFloor({
            resident_guid: this.entrancesModal.residentGuid,
            entrance: entrance,
            floor: floor
          });
          
          // Удаляем этаж из структуры
          const floors = this.entrancesStructure[entrance]?.floors || [];
          const index = floors.findIndex(f => String(f.floor) === String(floor));
          if (index !== -1) {
            floors.splice(index, 1);
          }
          
          this.showSucceedSnackbar("Этаж удален");
          await this.loadEntrancesData();
        } catch (e) {
          this.showErrorSnackbar("Ошибка удаления этажа");
        }
      });
    },
    getFlatsRange(entrance, floor) {
      const floors = this.entrancesStructure[entrance]?.floors || [];
      const floorItem = floors.find(f => f.floor === floor);
      const flatNums = floorItem?.flat_nums || [];
      
      if (!floorItem || !flatNums || flatNums.length === 0) {
        return "0";
      }
      const parts = [];
      
      // Обрабатываем обычные квартиры (не МОП)
      const regularFlats = flatNums.filter(n => {
        const numStr = String(n);
        return numStr !== 'МОП' && numStr !== '' && numStr !== 'null' && numStr !== 'undefined';
      });
      
      if (regularFlats.length > 0) {
        // Находим min и max из номеров квартир
        const nums = regularFlats.map(n => {
          // Извлекаем число из номера квартиры (например, "1-2-3" -> 3, или просто "6" -> 6)
          const numStr = n.toString();
          // Пробуем извлечь последнее число из строки
          const match = numStr.match(/\d+$/);
          if (match) {
            return parseInt(match[0]);
          }
          // Если не нашли, пробуем извлечь первое число
          const firstMatch = numStr.match(/^\d+/);
          return firstMatch ? parseInt(firstMatch[0]) : 0;
        }).filter(n => n > 0);
        
        if (nums.length > 0) {
          const min = Math.min(...nums);
          const max = Math.max(...nums);
          parts.push(min === max ? `${min}` : `${min}-${max}`);
        }
      }
      
      // Обрабатываем МОП
      // Считаем МОП из массива
      const mopCount = flatNums.filter(n => {
        const numStr = String(n);
        return numStr === 'МОП' || numStr === '' || numStr === 'null' || numStr === 'undefined';
      }).length;
      
      if (mopCount > 0) {
        // Если МОП больше одного, показываем количество в скобках
        if (mopCount > 1) {
          parts.push(`МОП(${mopCount})`);
        } else {
          parts.push('МОП');
        }
      }
      
      // Если ничего не найдено, но есть квартиры, показываем диапазон
      if (parts.length === 0 && flatNums.length > 0) {
        const count = flatNums.length;
        return count === 1 ? "1" : `1-${count}`;
      }
      
      return parts.join(', ');
    },
    editEntrance(entrance) {
      this.editEntranceModal.entrance = entrance;
      this.editEntranceModal.originalEntrance = entrance;
      this.editEntranceModal.show = true;
    },
    async confirmDeleteEntranceFromModal() {
      this.$q.dialog({
        title: 'Подтверждение',
        message: `Удалить подъезд ${this.editEntranceModal.originalEntrance}?`,
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          await deleteEntrance({
            resident_guid: this.entrancesModal.residentGuid,
            entrance: this.editEntranceModal.originalEntrance
          });
          delete this.entrancesStructure[this.editEntranceModal.originalEntrance];
          this.editEntranceModal.show = false;
          this.showSucceedSnackbar("Подъезд удален");
          await this.loadEntrancesData();
        } catch (e) {
          this.showErrorSnackbar("Ошибка удаления подъезда");
        }
      });
    },
    addFlatToTopFloor(entrance) {
      // Открываем модалку для добавления квартиры
      this.addFlatModal.entrance = entrance;
      this.addFlatModal.items = [{ floor: "", flat_num: "", is_mop: false }];
      this.addFlatModal.show = true;
    },
    closeAddFlatModal() {
      this.addFlatModal.show = false;
      this.addFlatModal.items = [{ floor: "", flat_num: "", is_mop: false }];
    },
    isRoofOrBasement(floor) {
      return String(floor) === '100' || String(floor) === '-1';
    },
    onMopToggle(item, index) {
      if (item.is_mop) {
        // Если включили МОП, очищаем номер квартиры
        item.flat_num = '';
      }
    },
    onEditMopToggle(item, index) {
      if (item.is_mop) {
        // Если включили МОП, очищаем номер квартиры
        item.flat_num = '';
      }
    },
    addFlatInputRow() {
      this.addFlatModal.items.push({ floor: "", flat_num: "", is_mop: false });
    },
    removeFlatInputRow(index) {
      if (this.addFlatModal.items.length > 1) {
        this.addFlatModal.items.splice(index, 1);
      }
    },
    addRoof() {
      if (!this.addFlatModal.entrance) return;
      
      // Проверяем, что кровля еще не добавлена в текущую модалку
      const hasRoofInModal = this.addFlatModal.items.some(item => String(item.floor) === '100');
      if (hasRoofInModal) {
        this.showWarningSnackbar("Кровля уже добавлена в список");
        return;
      }
      
      // Добавляем строку в таблицу с предзаполненными значениями (МОП)
      this.addFlatModal.items.push({
        floor: '100',
        flat_num: '',
        is_mop: true
      });
    },
    addBasement() {
      if (!this.addFlatModal.entrance) return;
      
      // Проверяем, что подвал еще не добавлен в текущую модалку
      const hasBasementInModal = this.addFlatModal.items.some(item => String(item.floor) === '-1');
      if (hasBasementInModal) {
        this.showWarningSnackbar("Подвал уже добавлен в список");
        return;
      }
      
      // Добавляем строку в таблицу с предзаполненными значениями (МОП)
      this.addFlatModal.items.push({
        floor: '-1',
        flat_num: '',
        is_mop: true
      });
    },
    openEditFlatsModal(entrance, floor) {
      this.editFlatsModal.entrance = entrance;
      this.editFlatsModal.floor = floor;
      
      // Загружаем существующие квартиры на этом этаже
      const floors = this.entrancesStructure[entrance]?.floors || [];
      const floorItem = floors.find(f => String(f.floor) === String(floor));
      
      const flatNums = floorItem?.flat_nums || [];
      const flatIds = floorItem?.flat_ids || [];
      
      if (flatNums.length > 0) {
        // Если есть номера квартир, используем их вместе с flat_ids
        this.editFlatsModal.items = flatNums.map((num, index) => {
          const numStr = String(num);
          const isMop = numStr === 'МОП' || numStr === '' || numStr === 'null' || numStr === 'undefined';
          return {
            flat_num: isMop ? '' : numStr,
            is_mop: isMop,
            flat_id: flatIds[index] || null
          };
        });
      } else {
        // Если ничего нет, создаем одну пустую строку
        this.editFlatsModal.items = [{ flat_num: "", is_mop: false, flat_id: null }];
      }
      
      this.editFlatsModal.show = true;
    },
    addEditFlatRow() {
      this.editFlatsModal.items.push({ flat_num: "", is_mop: false, flat_id: null });
    },
    removeEditFlatRow(index) {
      if (this.editFlatsModal.items.length > 1) {
        this.editFlatsModal.items.splice(index, 1);
      }
    },
    async saveEntranceEdit() {
      if (!this.editEntranceModal.entrance) {
        this.showWarningSnackbar("Введите номер подъезда");
        return;
      }
      
      const newEntrance = this.editEntranceModal.entrance;
      const oldEntrance = this.editEntranceModal.originalEntrance;
      
      if (newEntrance === oldEntrance) {
        this.editEntranceModal.show = false;
        return;
      }
      
      if (this.entrancesStructure[newEntrance]) {
        this.showWarningSnackbar("Подъезд с таким номером уже существует");
        return;
      }
      
      try {
        // Сохраняем структуру старого подъезда
        const floors = this.entrancesStructure[oldEntrance];
        
        // Удаляем старый подъезд из базы
        await deleteEntrance({
          resident_guid: this.entrancesModal.residentGuid,
          entrance: oldEntrance
        });
        
        // Переименовываем подъезд в структуре
        delete this.entrancesStructure[oldEntrance];
        this.entrancesStructure[newEntrance] = floors;
        
        // Обновляем все квартиры в этом подъезде
        if (floors && floors.floors) {
          for (const floorItem of floors.floors) {
            // Обновляем номера квартир, если они были сформированы автоматически
            if (floorItem.flat_nums && floorItem.flat_nums.length > 0) {
              floorItem.flat_nums = floorItem.flat_nums.map((num) => {
                // Если номер содержит старый номер подъезда, заменяем
                const numStr = num.toString();
                if (numStr.startsWith(oldEntrance + "-")) {
                  return numStr.replace(oldEntrance, newEntrance);
                }
                return num;
              });
            }
          }
        }
        
        // Сохраняем новый подъезд
        this.editEntranceModal.show = false;
        await this.saveEntranceStructure(newEntrance);
        this.showSucceedSnackbar("Подъезд переименован");
      } catch (e) {
        this.showErrorSnackbar("Ошибка переименования подъезда");
      }
    },
    editFloor(entrance, floorItem) {
      this.editFloorModal.entrance = entrance;
      this.editFloorModal.floor = floorItem.floor;
      this.editFloorModal.originalFloor = floorItem.floor;
      this.editFloorModal.show = true;
    },
    async saveFloorEdit() {
      if (!this.editFloorModal.floor) {
        this.showWarningSnackbar("Заполните номер этажа");
        return;
      }
      
      const floors = this.entrancesStructure[this.editFloorModal.entrance].floors;
      const floorItem = floors.find(f => f.floor === this.editFloorModal.originalFloor);
      
      if (!floorItem) {
        this.showWarningSnackbar("Этаж не найден");
        return;
      }
      
      // Если номер этажа изменился, проверяем уникальность
      if (this.editFloorModal.floor !== this.editFloorModal.originalFloor) {
        const existingFloor = floors.find(f => f.floor === this.editFloorModal.floor && f !== floorItem);
        if (existingFloor) {
          this.showWarningSnackbar("Этаж с таким номером уже существует");
          return;
        }
      }
      
      floorItem.floor = this.editFloorModal.floor;
      // flats_count вычисляется автоматически из длины flat_nums
      
      // Сортируем этажи по убыванию
      floors.sort((a, b) => parseInt(b.floor) - parseInt(a.floor));
      
      this.editFloorModal.show = false;
      await this.saveEntranceStructure(this.editFloorModal.entrance);
    },
    async confirmDeleteFloorFromModal() {
      this.$q.dialog({
        title: 'Подтверждение',
        message: `Удалить этаж ${this.editFloorModal.floor}?`,
        cancel: true,
        persistent: true
      }).onOk(async () => {
        const floors = this.entrancesStructure[this.editFloorModal.entrance].floors;
        const index = floors.findIndex(f => f.floor === this.editFloorModal.originalFloor);
        if (index !== -1) {
          floors.splice(index, 1);
          this.editFloorModal.show = false;
          await this.saveEntranceStructure(this.editFloorModal.entrance);
        }
      });
    },
    async saveEntranceStructure(entrance) {
      if (!this.entrancesModal.residentGuid) return;
      
      try {
        const floors = this.entrancesStructure[entrance]?.floors || [];
        
        // Проверяем, что есть хотя бы один этаж (floors_data обязателен)
        if (floors.length === 0) {
          this.showWarningSnackbar("Добавьте хотя бы один этаж с квартирами");
          return;
        }
        
        await saveEntrance({
          resident_guid: this.entrancesModal.residentGuid,
          entrance: entrance,
          floors_data: floors.map(f => ({
            floor: f.floor,
            flat_nums: f.flat_nums || [],
            flat_ids: f.flat_ids || []
          }))
        });
        this.showSucceedSnackbar("Структура подъезда сохранена");
        await this.loadEntrancesData();
      } catch (e) {
        this.showErrorSnackbar("Ошибка сохранения структуры подъезда");
      }
    },
    async saveAddFlat() {
      if (!this.addFlatModal.entrance || !this.addFlatModal.items || this.addFlatModal.items.length === 0) {
        this.showWarningSnackbar("Добавьте хотя бы одну квартиру");
        return;
      }
      
      // Фильтруем пустые строки и проверяем, что остались заполненные
      const validItems = this.addFlatModal.items.filter(item => {
        if (!item.floor || !String(item.floor).trim()) return false;
        // Для МОП flat_num может быть пустым, для обычных квартир - обязателен
        if (item.is_mop) return true;
        return item.flat_num && String(item.flat_num).trim();
      });
      
      if (validItems.length === 0) {
        this.showWarningSnackbar("Добавьте хотя бы одну квартиру с заполненными полями");
        return;
      }
      
      const entrance = this.addFlatModal.entrance;
      
      // Инициализируем структуру подъезда, если его нет
      if (!this.entrancesStructure[entrance]) {
        this.entrancesStructure[entrance] = { floors: [] };
      }
      
      const floors = this.entrancesStructure[entrance].floors;
      
      // Группируем квартиры по этажам (только валидные записи)
      const floorsMap = {};
      validItems.forEach(item => {
        const floor = String(item.floor).trim();
        // Для МОП передаем пустую строку, для обычных квартир - номер
        const flatNum = item.is_mop ? '' : String(item.flat_num).trim();
        
        if (!floorsMap[floor]) {
          floorsMap[floor] = [];
        }
        floorsMap[floor].push(flatNum);
      });
      
      // Обновляем или создаем этажи
      Object.keys(floorsMap).forEach(floor => {
        const existingFloor = floors.find(f => String(f.floor) === String(floor));
        const flatNums = floorsMap[floor];
        
        if (existingFloor) {
          // Если этаж уже есть, добавляем новые квартиры
          const existingNums = existingFloor.flat_nums || [];
          flatNums.forEach(num => {
            if (!existingNums.includes(num)) {
              existingNums.push(num);
            }
          });
          existingFloor.flat_nums = existingNums;
        } else {
          // Если этажа нет, создаем новый этаж
          floors.push({
            floor: floor,
            flat_nums: flatNums,
            flat_ids: []
          });
        }
      });
      
      // Сортируем этажи по убыванию
      floors.sort((a, b) => parseInt(b.floor) - parseInt(a.floor));
      
      this.closeAddFlatModal();
      await this.saveEntranceStructure(entrance);
    },
    async saveEditFlats() {
      if (!this.editFlatsModal.entrance || !this.editFlatsModal.floor) {
        this.showWarningSnackbar("Ошибка: не указан подъезд или этаж");
        return;
      }
      
      // Проверяем, что все номера заполнены (кроме МОП)
      const invalidItems = this.editFlatsModal.items.filter(item => !item.is_mop && (!item.flat_num || !item.flat_num.trim()));
      if (invalidItems.length > 0) {
        this.showWarningSnackbar("Заполните все номера квартир");
        return;
      }
      
      const entrance = this.editFlatsModal.entrance;
      const floor = String(this.editFlatsModal.floor);
      
      // Инициализируем структуру подъезда, если его нет
      if (!this.entrancesStructure[entrance]) {
        this.entrancesStructure[entrance] = { floors: [] };
      }
      
      const floors = this.entrancesStructure[entrance].floors;
      const floorItem = floors.find(f => String(f.floor) === String(floor));
      
      // Для МОП передаем пустую строку, для обычных квартир - номер
      // Сохраняем flat_ids для точного обновления/удаления
      const flatNums = this.editFlatsModal.items.map(item => {
        if (item.is_mop) {
          return ''; // Пустая строка для МОП
        }
        const num = String(item.flat_num).trim();
        return num || ''; // Если номер пустой, тоже пустая строка
      });
      
      const flatIds = this.editFlatsModal.items.map(item => item.flat_id || null);
      
      if (floorItem) {
        // Обновляем существующий этаж
        floorItem.flat_nums = flatNums;
        floorItem.flat_ids = flatIds;
      } else {
        // Создаем новый этаж
          floors.push({
            floor: floor,
            flat_nums: flatNums,
            flat_ids: flatIds
          });
        // Сортируем этажи по убыванию
        floors.sort((a, b) => parseInt(b.floor) - parseInt(a.floor));
      }
      
      this.editFlatsModal.show = false;
      await this.saveEntranceStructure(entrance);
    },
    async openBindModal(row) {
      this.bindModal.currentResident = row;
      this.bindModal.search = "";
      this.bindModal.showOnlyUnbound = false;
      this.bindModal.loading = true;
      this.bindModal.show = true;
      
      try {
        // Загружаем список ЖК для привязки через функцию БД
        // Функция возвращает только ЖК с guid, исключая текущий ЖК, с полем is_bound
        this.bindModal.residentsList = await getResidentsForBind(row.resident_id);
      } catch (e) {
        this.showErrorSnackbar("Ошибка загрузки списка ЖК");
        this.bindModal.residentsList = [];
      } finally {
        this.bindModal.loading = false;
      }
    },
    async openBoundInfoModal(row) {
      this.boundInfoModal.currentResident = row;
      this.boundInfoModal.loading = false;
      this.boundInfoModal.show = true;
    },
    selectResident(row) {
      // Если кликнули на уже выбранную строку - снимаем выбор
      if (this.bindModal.selectedResident && this.bindModal.selectedResident.resident_id === row.resident_id) {
        this.bindModal.selectedResident = null;
      } else {
        this.bindModal.selectedResident = row;
      }
    },
    closeBindModal() {
      this.bindModal.show = false;
      this.bindModal.selectedResident = null;
    },
    async confirmBindResident() {
      if (!this.bindModal.currentResident || !this.bindModal.selectedResident) return;
      
      await this.bindResident(this.bindModal.selectedResident);
    },
    async bindResident(selectedResident) {
      if (!this.bindModal.currentResident || !selectedResident) return;
      
      try {
        // Сохраняем привязку через обновление текущего ЖК
        // В поле guid сохраняем guid выбранного ЖК
        const payload = {
          resident_id: this.bindModal.currentResident.resident_id,
          resident_name: this.bindModal.currentResident.resident_name,
          city_id: this.bindModal.currentResident.city_id,
          resident_class_id: this.bindModal.currentResident.resident_class_id,
          is_active: 1,
          resident_address: this.bindModal.currentResident.resident_address || null,
          guid: selectedResident.guid,
          is_fake_guid: false
        };
        
        const response = await saveResidentItem(payload);
        // Проверяем, есть ли ошибка в ответе (status: false означает ошибку от бэкенда)
        if (response?.status === false || response?.error) {
          const errorMessage = response?.message || response?.error || "Ошибка привязки ЖК";
          if (errorMessage.includes("Данные ЖК уже привязан") || errorMessage.includes("ЖК уже привязан")) {
            this.showWarningSnackbar("Данные ЖК уже привязан");
          } else {
            this.showErrorSnackbar(errorMessage);
          }
          return;
        }
        this.showSucceedSnackbar("ЖК успешно привязан");
        this.closeBindModal();
        await this.fetchResidents(); // Обновляем список
      } catch (e) {
        // Проверяем, содержит ли ошибка сообщение от базы данных
        const errorMessage = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Ошибка привязки ЖК";
        if (errorMessage.includes("Данные ЖК уже привязан") || errorMessage.includes("ЖК уже привязан")) {
          this.showWarningSnackbar("Данные ЖК уже привязан");
        } else {
          this.showErrorSnackbar(errorMessage);
        }
      }
    },
    async unbindResidentFromModal() {
      if (!this.bindModal.currentResident) return;
      
      this.$q.dialog({
        title: 'Подтверждение',
        message: 'Отвязать ЖК?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          const row = this.bindModal.currentResident;
          const payload = {
            resident_id: row.resident_id,
            resident_name: row.resident_name,
            city_id: row.city_id,
            resident_class_id: row.resident_class_id,
            is_active: 1,
            resident_address: row.resident_address || null,
            guid: null,
            is_fake_guid: false
          };
          
          await saveResidentItem(payload);
          this.showSucceedSnackbar("ЖК отвязан");
          this.closeBindModal();
          await this.fetchResidents(); // Обновляем список
        } catch (e) {
          this.showErrorSnackbar("Ошибка отвязки ЖК");
        }
      });
    },
    async unbindResident() {
      if (!this.boundInfoModal.currentResident) return;
      
      const $q = this.$q || this.$root.$q;
      if (!$q || !$q.dialog) {
        // Fallback: просто выполняем отвязку без диалога
        try {
          const row = this.boundInfoModal.currentResident;
          const payload = {
            resident_id: row.resident_id,
            resident_name: row.resident_name,
            city_id: row.city_id,
            resident_class_id: row.resident_class_id,
            is_active: 1,
            resident_address: row.resident_address || null,
            guid: null,
            is_fake_guid: false
          };
          
          await saveResidentItem(payload);
          this.showSucceedSnackbar("ЖК отвязан");
          this.boundInfoModal.show = false;
          await this.fetchResidents();
        } catch (e) {
          this.showErrorSnackbar("Ошибка отвязки ЖК");
        }
        return;
      }
      
      $q.dialog({
        title: 'Подтверждение',
        message: 'Отвязать ЖК?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          const row = this.boundInfoModal.currentResident;
          const payload = {
            resident_id: row.resident_id,
            resident_name: row.resident_name,
            city_id: row.city_id,
            resident_class_id: row.resident_class_id,
            is_active: 1,
            resident_address: row.resident_address || null,
            guid: null,
            is_fake_guid: false
          };
          
          await saveResidentItem(payload);
          this.showSucceedSnackbar("ЖК отвязан");
          this.boundInfoModal.show = false;
          await this.fetchResidents(); // Обновляем список
        } catch (e) {
          this.showErrorSnackbar("Ошибка отвязки ЖК");
        }
      });
    },
    isResidentBound(row) {
      // Проверяем, привязан ли ЖК к другому ЖК
      // ЖК привязан, если есть другой ЖК, у которого его собственный guid равен guid текущего ЖК
      // (т.е. guid текущего ЖК указывает на guid другого ЖК, к которому он привязан)
      if (!row.guid) return false;
      
      // Ищем ЖК, у которого его собственный guid равен guid из текущего ЖК
      // Если такой найден, значит текущий ЖК привязан к найденному ЖК
      return this.allRowsData.some(other => 
        other.resident_id !== row.resident_id && 
        other.guid === row.guid
      );
    },
    generateUniqueGuid() {
      // Генерируем уникальный GUID
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      // Fallback для старых браузеров
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    async createGuidWithoutBindingFromModal() {
      if (!this.bindModal.currentResident) return;
      
      this.$q.dialog({
        title: 'Подтверждение',
        message: 'Создать GUID без привязки?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        await this.createGuidWithoutBinding(this.bindModal.currentResident, true);
      });
    },
    async createGuidWithoutBinding(row, closeModal = false) {
      if (!row) return;
      
      try {
        // Генерируем уникальный GUID
        const newGuid = this.generateUniqueGuid();
        
        // Обновляем resident_tab с новым GUID
        const payload = {
          resident_id: row.resident_id,
          resident_name: row.resident_name,
          city_id: row.city_id,
          resident_class_id: row.resident_class_id,
          is_active: 1,
          resident_address: row.resident_address || null,
          guid: newGuid,
          is_fake_guid: true
        };
        
        const response = await saveResidentItem(payload);
        // Проверяем, есть ли ошибка в ответе
        if (response?.status === false || response?.error) {
          const errorMessage = response?.message || response?.error || "Ошибка создания GUID";
          this.showErrorSnackbar(errorMessage);
          return;
        }
        this.showSucceedSnackbar("GUID успешно создан");
        if (closeModal) {
          this.closeBindModal();
        }
        await this.fetchResidents(); // Обновляем список
      } catch (e) {
        const errorMessage = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Ошибка создания GUID";
        this.showErrorSnackbar(errorMessage);
      }
    }
  },
  watch: {
    'userData.city_id'() {
      // Автоматически обновляем список при изменении города в шапке
      this.fetchResidents();
    }
  },
  async mounted() {
    await this.loadDictionaries();
    await this.fetchResidents();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  setup() {
    const { showSucceedSnackbar, showWarningSnackbar, showErrorSnackbar } = useCustomSnackbar();
    const appStore = useAppStore();
    const { userData, cities } = storeToRefs(appStore);
    const $q = useQuasar();
    return { 
      showSucceedSnackbar, 
      showWarningSnackbar, 
      showErrorSnackbar,
      userData,
      cities,
      $q
    };
  }
};
</script>

<style scoped>
.resident-dictionary {
  padding: 8px;
}

.excel-table-container {
  height: calc(100vh - 400px);
  min-height: 500px;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: #fafafa;
  border: 1px solid #e0e0e0;
}

.excel-table-wrapper {
  width: 100%;
  height: 100%;
  overflow: auto;
  border-radius: 8px;
}

.excel-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
}

.excel-table th,
.excel-table td {
  border-right: 1px solid #e8e8e8;
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
  min-width: 120px;
  height: 40px;
  background: white;
  transition: all 0.2s ease;
}

.excel-table th:first-child,
.excel-table td:first-child {
  border-left: 1px solid #e8e8e8;
}

.excel-header-cell {
  background: linear-gradient(180deg, #f8f9fa 0%, #f0f2f5 100%);
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
  height: 50px;
  min-height: 50px;
  color: #424242;
  font-size: 13px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 2px solid #e0e0e0;
}

.excel-header-cell.add-floor-header {
  background: linear-gradient(180deg, #f8f9fa 0%, #f0f2f5 100%);
  border: 1px solid #1976d2;
  border-radius: 6px;
  cursor: pointer;
  min-width: 150px;
  color: #1976d2;
  font-weight: 500;
  transition: all 0.3s ease;
}

.excel-header-cell.add-floor-header:hover {
  background: linear-gradient(180deg, #f0f2f5 0%, #e9ecef 100%);
  border-color: #1565c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(25, 118, 210, 0.2);
}

.excel-header-cell.add-entrance-header {
  background: linear-gradient(180deg, #f8f9fa 0%, #f0f2f5 100%);
  border: 1px solid #1976d2;
  border-radius: 6px;
  cursor: pointer;
  min-width: 150px;
  color: #1976d2;
  font-weight: 500;
  transition: all 0.3s ease;
}

.excel-header-cell.add-entrance-header:hover {
  background: linear-gradient(180deg, #f0f2f5 0%, #e9ecef 100%);
  border-color: #1565c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(25, 118, 210, 0.2);
}

.entrance-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.entrance-title {
  font-weight: 600;
  font-size: 13px;
  color: #1976d2;
}

.excel-row {
  transition: all 0.2s ease;
}

.excel-row:hover {
  background: #f8f9fa;
}

.excel-row:hover .excel-cell {
  background: #f8f9fa;
}

.excel-row.row-highlighted {
  background: #e3f2fd !important;
}

.excel-row.row-highlighted .excel-cell {
  background: #e3f2fd !important;
  box-shadow: inset 0 0 0 2px #1976d2;
}

.floor-cell {
  background: linear-gradient(90deg, #f8f9fa 0%, #f0f2f5 100%);
  font-weight: 600;
  min-width: 150px;
  position: sticky;
  left: 0;
  z-index: 5;
  cursor: pointer;
  color: #424242;
  font-size: 13px;
  border-right: 2px solid #e0e0e0;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
}

.floor-cell:hover {
  background: linear-gradient(90deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
}

.excel-row.row-highlighted .floor-cell {
  background: linear-gradient(90deg, #90caf9 0%, #64b5f6 100%) !important;
  color: #0d47a1;
  box-shadow: inset 0 0 0 2px #1976d2, 2px 0 4px rgba(0, 0, 0, 0.1);
}

.flats-cell {
  background: white;
  color: #757575;
  font-size: 12px;
}

.flats-cell.has-flats {
  background: white;
  color: #1976d2;
  font-weight: 500;
}

.flats-cell.clickable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.flats-cell.flats-cell-hovered {
  background: #e3f2fd !important;
  box-shadow: inset 0 0 0 2px #1976d2;
  transition: all 0.2s ease;
}

.flats-cell.flats-cell-hovered.has-flats {
  background: #bbdefb !important;
  color: #0d47a1;
  font-weight: 600;
}

.flats-cell.clickable:hover {
  background: #e3f2fd !important;
}

.excel-row.row-highlighted .flats-cell.has-flats {
  background: #f0f0f0 !important;
  color: #1976d2;
}

.excel-row.row-highlighted .flats-cell {
  background: #e3f2fd !important;
}

.empty-cell {
  background: transparent;
  border: none;
  min-width: 150px;
}

.floor-number {
  font-weight: 600;
  color: inherit;
  font-size: 13px;
}

.flats-in-floor {
  text-align: center;
  font-size: 12px;
}

/* Стили для строки добавления квартир */
.add-flats-row {
  background: #f8f9fa;
}

.add-flats-header-cell {
  background: linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%);
  font-weight: 500;
  font-size: 12px;
  color: #6c757d;
}

.add-flats-label {
  font-size: 12px;
  color: #6c757d;
}

.add-flats-cell {
  background: #f8f9fa;
  text-align: center;
}

.add-flat-btn {
  font-size: 11px;
}

/* Левая колонка: этажи */
.floors-column {
  min-width: 120px;
  max-width: 120px;
  border-right: none;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
}

.floors-scroll-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  /* Синхронизация скролла с подъездами */
  scroll-behavior: auto;
}

.floor-row-header {
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #1976d2;
  border-radius: 4px;
  padding: 8px;
  background: #e3f2fd;
  color: #1976d2;
  cursor: pointer;
  margin-bottom: 0;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  flex-shrink: 0;
  border-bottom: 2px solid #bdbdbd;
  position: relative;
}


.floor-row-header.add-floor-btn-header:hover {
  background: #bbdefb;
  border-color: #1565c0;
}

.floors-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.floor-row {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  border: none;
  border-bottom: 2px solid #bdbdbd;
  padding: 6px 8px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
}

.floor-row.floor-label:hover {
  background: #e3f2fd;
  z-index: 5;
}

.floor-row.floor-label:nth-child(even) {
  background: #fafafa;
}

.floor-row.floor-label:nth-child(even):hover {
  background: #e3f2fd;
  z-index: 5;
}

/* Выделение всей строки этажа при наведении */
.floor-row.floor-label:hover {
  background: #bbdefb !important;
  box-shadow: 0 0 0 2px #1976d2;
  z-index: 5;
}

.floor-number {
  font-weight: 600;
  color: #424242;
  font-size: 12px;
}

/* Правая колонка: подъезды */
.entrances-column {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.entrances-scroll-container {
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  padding: 10px 0;
}

.entrances-scroll {
  display: flex;
  flex-direction: row;
  gap: 12px;
  min-width: fit-content;
  padding-bottom: 10px;
  align-items: flex-start;
}

.entrance-column {
  min-width: 150px;
  max-width: 150px;
  border: none;
  padding: 10px;
  background: transparent;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
}

.entrance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #bdbdbd;
  cursor: pointer;
  flex-shrink: 0;
  height: 50px;
  min-height: 50px;
  background: #f5f5f5;
  padding: 8px;
  border-radius: 0;
  position: relative;
}

.entrance-title {
  font-weight: bold;
  font-size: 14px;
  color: #1976d2;
}

.entrance-title.clickable {
  cursor: pointer;
}

/* Стили для таблицы в модалке добавления/редактирования квартир */
.flats-input-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}

.flats-input-table th,
.flats-input-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.flats-input-table th {
  background: #f5f5f5;
  font-weight: 600;
  font-size: 12px;
  color: #424242;
}

.flats-input-table tbody tr:hover {
  background: #fafafa;
}

.flats-input-table td {
  padding: 4px 8px;
}

.flats-by-floors-container {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
}

.flats-floor-row {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  border: none;
  border-bottom: 2px solid #bdbdbd;
  padding: 4px 8px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #999;
  transition: all 0.2s;
  flex-shrink: 0;
  position: relative;
}

.flats-floor-row:nth-child(even) {
  background: #fafafa;
}

.flats-floor-row.has-flats {
  background: #e3f2fd;
  color: #1976d2;
  font-weight: 500;
}

.flats-floor-row.has-flats:nth-child(even) {
  background: #bbdefb;
}

.flats-floor-row.has-flats:hover {
  background: #90caf9;
  cursor: pointer;
}

/* Подсветка квартир при наведении на этаж */
.floor-row.floor-label:hover {
  background: #bbdefb !important;
  box-shadow: 0 0 0 2px #1976d2;
  z-index: 5;
}

.flats-floor-row.floor-highlighted {
  background: #bbdefb !important;
  box-shadow: 0 0 0 2px #1976d2;
  z-index: 5;
}

.flats-floor-row.floor-highlighted.has-flats {
  background: #90caf9 !important;
  box-shadow: 0 0 0 2px #1976d2;
}

.flats-in-floor {
  text-align: center;
}

.add-entrance-column {
  min-width: 150px;
  max-width: 150px;
  display: flex;
  align-items: flex-start;
  padding-top: 0;
}

.add-entrance-btn-container {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #1976d2;
  border-radius: 4px;
  padding: 20px;
  background: #e3f2fd;
  color: #1976d2;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s;
}

.add-entrance-btn-container:hover {
  background: #bbdefb;
  border-color: #1565c0;
}

/* Улучшенная видимость соответствия этажей и квартир */
.floor-row.floor-label,
.flats-floor-row {
  position: relative;
}

.floor-row.floor-label::after,
.flats-floor-row::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: background 0.2s;
}

.floor-row.floor-label:hover::after,
.flats-floor-row.has-flats:hover::after {
  background: #1976d2;
}

.clickable {
  cursor: pointer;
}

/* Современный дизайн модалки */
.entrances-modal-card {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

/* Улучшенный скролл */
.excel-table-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.excel-table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.excel-table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
  transition: background 0.2s;
}

.excel-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Плавные переходы для всех интерактивных элементов */
.excel-table th,
.excel-table td {
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

/* Улучшенная читаемость */
.excel-table {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Тонкие тени для глубины */
.excel-table th {
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.8);
}

/* Подсветка выбранной строки в таблице привязки */
.selected-row {
  background-color: #e3f2fd !important;
}

.selected-row:hover {
  background-color: #bbdefb !important;
}

/* Отключаем автоматическое преобразование текста кнопок в верхний регистр */
.resident-dictionary .q-btn {
  text-transform: none !important;
}
</style>
